/**
 * Referral cookie helper — set / read / clear the `profileia_ref` cookie.
 *
 * Spec ref: SPEC-094 §1.2 (Cookie de Referido Extendida),
 *           SPEC-NEGOCIO §8.3 (Redirecciones — referral attribution cookie
 *           30 días, SameSite=Lax, Secure si HTTPS).
 *
 * The cookie `profileia_ref` is the SAME cookie already used by the
 * `/r/[code]` referral redirect page (see `frontend/src/app/r/[code]/
 * page.tsx` lines 110-115). The backend register controller reads it
 * via the referral-attribution middleware (falls back to `?ref=CODE`
 * query param if the cookie is absent — see `register/page.tsx` F-26 +
 * SPEC-043 fix).
 *
 * SPEC-094 extends the routes that set this cookie to:
 *   - `/market?ref=USERNAME` (public catalog with referral attribution)
 *   - `/market/producto/[storeSlug]/[productSlug]?ref=USERNAME`
 *     (public product detail with referral attribution)
 *   - `/p/{username}` (public profile visit — username IS the referrer)
 *
 * §6 Anti-Invención:
 *   - Cookie name `profileia_ref` mirrors the existing `/r/[code]` impl
 *     VERBATIM (no invented name). Single source of truth — the backend
 *     reads this exact cookie name.
 *   - Max-Age=2592000 (30 días) mirrors SPEC-NEGOCIO §8.3 + the existing
 *     `/r/[code]` impl VERBATIM (no invented threshold).
 *   - SameSite=Lax mirrors the existing `/r/[code]` impl VERBATIM (the
 *     /r/[code] → /register redirect is a same-site navigation).
 *   - `Secure` ONLY on HTTPS — mirrors the existing `/r/[code]` impl
 *     VERBATIM (4D-MED-001 fix: in dev http://localhost, the `Secure`
 *     attribute caused the browser to silently drop the cookie — only
 *     emit `Secure` when the protocol is HTTPS).
 *   - `Path=/` mirrors the existing `/r/[code]` impl VERBATIM (sent on
 *     every request to any path).
 *
 * Anti-P-041: NO `as any`. The cookie value is `encodeURIComponent`'d
 * before being set + `decodeURIComponent`'d when read.
 * Anti-P-042: NO `console.*` in production.
 */

/**
 * Cookie name — VERBATIM from the existing `/r/[code]` impl. The backend
 * reads this exact name (register controller's referral-attribution
 * middleware).
 */
export const REFERRAL_COOKIE_NAME = 'profileia_ref';

/**
 * Cookie max-age in seconds — 30 days. Mirrors SPEC-NEGOCIO §8.3 +
 * the existing `/r/[code]` impl VERBATIM (no invented threshold).
 */
export const REFERRAL_COOKIE_MAX_AGE_SECONDS = 2592000;

/**
 * Set the `profileia_ref` cookie with the given referral code.
 *
 * Mirrors the cookie-setting logic in `frontend/src/app/r/[code]/page.tsx`
 * lines 110-115 VERBATIM. The cookie is:
 *   - Path=/             → sent on every request to any path.
 *   - Max-Age=2592000    → 30 days (SPEC §8.3 attribution window).
 *   - SameSite=Lax       → sent on top-level navigations (same-site).
 *   - Secure (prod only) → only sent over HTTPS (production). In dev
 *                          (http://localhost), the `Secure` attribute
 *                          caused the browser to SILENTLY DROP the
 *                          cookie (4D-MED-001 fix). Only emit `Secure`
 *                          when the protocol is HTTPS.
 *
 * SSR-safe: this function is a no-op when called on the server (no
 * `document`).
 *
 * @param code The referral code (typically a username or the referrer's
 *             `User.referral.code`). Will be `encodeURIComponent`'d
 *             before being stored.
 */
export function setReferralCookie(code: string): void {
  if (typeof document === 'undefined') return;
  if (!code) return; // Don't set an empty cookie — would overwrite a valid one.
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(code)}; Path=/; Max-Age=${REFERRAL_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${isHttps ? '; Secure' : ''}`;
}

/**
 * Read the `profileia_ref` cookie.
 *
 * Returns the decoded referral code if the cookie exists, or `null` if
 * it doesn't (or if called on the server — SSR-safe).
 *
 * If the cookie value is malformed (e.g., the URI component is invalid),
 * returns `null` instead of throwing — same defensive pattern as the
 * existing `register/page.tsx` cookie reader (lines 110-121).
 */
export function getReferralCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)profileia_ref=([^;]+)/);
  if (!match || !match[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // Malformed cookie value — ignore silently. The user can still type
    // the code manually on /register. Mirrors the defensive pattern in
    // `register/page.tsx` (lines 110-121).
    return null;
  }
}

/**
 * Clear the `profileia_ref` cookie (set Max-Age=0 so the browser
 * deletes it immediately).
 *
 * Used in rare cases (e.g., admin testing) — not currently called from
 * any page. Provided for completeness + symmetry with the existing
 * `setReferralCookie` helper. Mirrors the same Path=/ + SameSite=Lax +
 * Secure-if-HTTPS attributes so the browser matches + deletes the
 * cookie correctly (browsers only delete a cookie if the deletion
 * request's Path + Domain match the original — same attributes
 * ensure the match).
 */
export function clearReferralCookie(): void {
  if (typeof document === 'undefined') return;
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = `${REFERRAL_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${isHttps ? '; Secure' : ''}`;
}
